import { useNavigate } from 'react-router';
import {getMagniProfileStats, formatDuration, getGuestProfileStats, formatTime } from '../utils/commonFunctions';
import { LOCATION_ICONS,LOCATIONS, COLOR_PALETTE } from '../utils/constants';
import React from 'react';
import { useOutletContext } from 'react-router';
import { EventData } from './Shared/Types';

interface UserProfileProps {
    user: any; 
}

interface TripProps {
    date: Date;
    startTime: Date;
    fromLocation: string;
    events: [],
    duration: 0,
}

interface ProfileContext {
    user: {
        id: number;
        name: string;
        type: string;
        trips: TripProps[];
    };
}

export const UserProfile = ({user}:UserProfileProps) => {
    const nav = useNavigate();
    const colorIndex = user.id % COLOR_PALETTE.length;
    const avatarColors = COLOR_PALETTE[colorIndex < 0? 0 : colorIndex];

    return (
      <div className="profile-container-apple">
        <div className="profile-header-apple">
          <button className="back-button-apple" onClick={() => nav(-1)}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
                 xmlns="http://www.w3.org/2000/svg">
              <path d="M15.41 7.41L14 6L8 12L14 18L15.41 16.59L10.83 12L15.41 7.41Z"
                    fill="currentColor"/>
            </svg>
          </button>
          <h1 className="profile-title-apple">Profile</h1>
          <div style={{width: 24}}></div>
        </div>
        <div className="user-card-apple">
          <div 
            className="user-avatar-apple" 
            style={{ backgroundColor: avatarColors.bg, color: avatarColors.initial }}
          >
            {user.name.charAt(0)}
          </div>
          <div className="user-info-apple">
            <h2 className="user-name-apple">{user.name}</h2>
            <div className={`user-type-badge-apple ${user.type.toLowerCase()}`}>
              {user.type}
            </div>
          </div>
        </div>
        {user.type === 'Magni' ? (
          <MagniProfile
          user={user}
          />
        ) : (
          <GuestProfile user={user} />
        )}
      </div>
    );
  };

  const MagniProfile = ( {user}:UserProfileProps) => {

    const [selectedDay, setSelectedDay] = React.useState<number | null>(null);
    const monthOptions = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ];
    const weekdayOptions = [
      "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"
    ];
    const [profileTab, setProfileTab] = React.useState('locations');
    const [dateFilter, setDateFilter] = React.useState('week');
    const [profileMonth, setProfileMonth] = React.useState<number | null>(null);
    const [profileYear, setProfileYear] = React.useState<number | null>(null);

    const currentYear = new Date().getFullYear();
    const yearOptions = [];
    for (let y = currentYear - 5; y <= currentYear + 5; y++) {
      yearOptions.push(y);
    }
    
    // Get profile stats based on current filter settings
    const { topLocations, timeOutOfOffice } = getMagniProfileStats(
      user, dateFilter.toLowerCase(), profileMonth, profileYear
    );
    
    const totalOut = timeOutOfOffice.reduce((sum, trip) => sum + trip.duration, 0);
    const avgOut = timeOutOfOffice.length ? totalOut / timeOutOfOffice.length : 0;
    
    // Group time entries by date for the mini calendar view
    const groupedEntries = React.useMemo(() => {
    const groups = new Map<string, { date: Date, trips: any[] }>();
      timeOutOfOffice.forEach(trip => {
        const dateStr:string = trip.date.toLocaleDateString();
        if (!groups.has(dateStr)) {
          groups.set(dateStr, {
            date: trip.date ,
            trips: []
          });
        }
        groups.get(dateStr)!.trips.push(trip);
      });
      return Array.from(groups.values());
    }, [timeOutOfOffice]);
    
    return (
      <>
        <div className="profile-tabs-apple">
          <button 
            className={`profile-tab-apple ${profileTab === 'locations' ? 'active' : ''}`}
            onClick={() => setProfileTab('locations')}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                 xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22C12 22 19 14.25 19 9C19 5.13 15.87 2 11.99 2ZM12 11.5C10.62 11.5 9.5 10.38 9.5 9C9.5 7.62 10.62 6.5 12 6.5C13.38 6.5 14.5 7.62 14.5 9C14.5 10.38 13.38 11.5 12 11.5Z" fill="currentColor"/>
            </svg>
            Locations
          </button>
          <button 
            className={`profile-tab-apple ${profileTab === 'time' ? 'active' : ''}`}
            onClick={() => setProfileTab('time')}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                 xmlns="http://www.w3.org/2000/svg">
              <path d="M11.99 2C6.47 2 2 6.48 2 12C2 17.52 6.47 22 11.99 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 11.99 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20ZM12.5 7H11V13L16.25 16.15L17 14.92L12.5 12.25V7Z" fill="currentColor"/>
            </svg>
            Time Out
          </button>
        </div>
        <div className="date-filters-apple">
          <button 
            className={`date-filter-apple ${dateFilter === 'week' ? 'active' : ''}`}
            onClick={() => setDateFilter('week')}
          >
            Week
          </button>
          <button 
            className={`date-filter-apple ${dateFilter === 'month' ? 'active' : ''}`}
            onClick={() => setDateFilter('month')}
          >
            Month
          </button>
          <button 
            className={`date-filter-apple ${dateFilter === 'year' ? 'active' : ''}`}
            onClick={() => setDateFilter('year')}
          >
            Year
          </button>
          <button 
            className={`date-filter-apple ${dateFilter === 'all' ? 'active' : ''}`}
            onClick={() => setDateFilter('all')}
          >
            All Time
          </button>
        </div>
        <div className="custom-date-selector-apple">
          <select 
            className="date-select-apple"
            value={profileMonth ?? ''}
            onChange={e => setProfileMonth(parseInt(e.target.value) || null)}
          >
            <option value="">Any Month</option>
            {monthOptions.map((m, i) => <option key={i} value={i}>{m}</option>)}
          </select>
          <select 
            className="date-select-apple"
            value={profileYear ?? ''}
            onChange={e => setProfileYear(parseInt(e.target.value) || null)}
          >
            <option value="">Any Year</option>
            {yearOptions.map((y, i) => <option key={i} value={y}>{y}</option>)}
          </select>
        </div>
        {profileTab === 'locations' ? (
          <div>
            <h3 className="section-title-apple">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                   xmlns="http://www.w3.org/2000/svg" className="section-icon-apple">
                <path d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22C12 22 19 14.25 19 9C19 5.13 15.87 2 11.99 2ZM12 11.5C10.62 11.5 9.5 10.38 9.5 9C9.5 7.62 10.62 6.5 12 6.5C13.38 6.5 14.5 7.62 14.5 9C14.5 10.38 13.38 11.5 12 11.5Z" fill="currentColor"/>
              </svg>
              Top 5 Most Visited Places
            </h3>
            {topLocations.length === 0 ? (
              <div className="no-data-apple">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none"
                     xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22C12 22 19 14.25 19 9C19 5.13 15.87 2 11.99 2ZM12 11.5C10.62 11.5 9.5 10.38 9.5 9C9.5 7.62 10.62 6.5 12 6.5C13.38 6.5 14.5 7.62 14.5 9C14.5 10.38 13.38 11.5 12 11.5Z" fill="currentColor"/>
                </svg>
                <p>No location data available for this period</p>
              </div>
            ) : (
              <div className="location-cards-apple">
                {topLocations.map((loc:any, i) => {
                  const total = topLocations.reduce((sum, l:any) => sum + l.visits, 0);
                  const pct = Math.round((loc.visits / total) * 100);
                  return (
                    <div key={i} className="location-card-apple">
                      <div className="location-info-apple">
                        <div className="location-name-apple">
                          <span className="location-icon-apple">{LOCATION_ICONS[loc.name] || "📍"}</span>
                          {loc.name}
                        </div>
                        <div className="location-visits-apple">
                          {loc.visits} visit{loc.visits !== 1 ? 's' : ''}
                        </div>
                      </div>
                      <div className="percentage-container-apple">
                        <div className="percentage-value-apple">{pct}%</div>
                        <div className="percentage-bar-apple">
                          <div className="percentage-fill-apple" style={{ width: `${pct}%` }}></div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <div>
            <h3 className="section-title-apple">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                   xmlns="http://www.w3.org/2000/svg" className="section-icon-apple">
                <path d="M11.99 2C6.47 2 2 6.48 2 12C2 17.52 6.47 22 11.99 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 11.99 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20Z" fill="currentColor"/>
              </svg>
              Time Out of Office
            </h3>
            <div className="time-summary-apple">
              <div className="summary-card-apple">
                <div className="summary-icon-apple">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                       xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 11.99 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 11.99 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20ZM15.88 8.29L10 14.17L8.12 12.29C7.73 11.9 7.1 11.9 6.71 12.29C6.32 12.68 6.32 13.31 6.71 13.7L9.3 16.29C9.69 16.68 10.32 16.68 10.71 16.29L17.3 9.7C17.69 9.31 17.69 8.68 17.3 8.29C16.91 7.9 16.27 7.9 15.88 8.29Z" fill="currentColor"/>
                  </svg>
                </div>
                <div className="summary-value-apple">{timeOutOfOffice.length}</div>
                <div className="summary-label-apple">Times Out</div>
              </div>
              <div className="summary-card-apple">
                <div className="summary-icon-apple">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                       xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 11.99 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 11.99 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20ZM12.5 7H11V13L16.25 16.15L17 14.92L12.5 12.25V7Z" fill="currentColor"/>
                  </svg>
                </div>
                <div className="summary-value-apple">{formatDuration(totalOut)}</div>
                <div className="summary-label-apple">Total Time</div>
              </div>
              <div className="summary-card-apple">
                <div className="summary-icon-apple">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                       xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 11.99 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 11.99 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20ZM12.5 7H11V13L16.25 16.15L17 14.92L12.5 12.25V7Z" fill="currentColor"/>
                  </svg>
                </div>
                <div className="summary-value-apple">{formatDuration(avgOut)}</div>
                <div className="summary-label-apple">Average Time</div>
              </div>
            </div>
            
            {timeOutOfOffice.length === 0 ? (
              <div className="no-data-apple">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none"
                     xmlns="http://www.w3.org/2000/svg">
                  <path d="M11 15H13V17H11V15ZM11 7H13V13H11V7ZM11.99 2C6.47 2 2 6.48 2 12C2 17.52 6.47 22 11.99 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 11.99 2ZM12 20C7.58 20 4 16.41 4 12C4 7.58 7.58 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20Z" fill="currentColor"/>
                </svg>
                <p>No time out data available for this period</p>
              </div>
            ) : (
              <>
                {/* Mini Calendar View */}
                <div className="mini-calendar-container">
                  {groupedEntries.map((group, idx) => {
                    const date = group.date;
                    const totalTime = group.trips.reduce((sum:any, trip:any) => sum + trip.duration, 0);
                    return (
                      <div 
                        key={idx} 
                        className="mini-calendar-day"
                        onClick={() => setSelectedDay(selectedDay === idx ? null : idx)}
                      >
                        <div className="mini-calendar-weekday">
                          {weekdayOptions[date.getDay()].substring(0, 3)}
                        </div>
                        <div className="mini-calendar-date">{date.getDate()}</div>
                        <div className="mini-calendar-month">
                          {monthOptions[date.getMonth()].substring(0, 3)}
                        </div>
                        <div className="mini-calendar-time">
                          <div className="mini-calendar-time-badge">
                            {formatDuration(totalTime)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Selected Day Detail View */}
                {selectedDay !== null && (
                  <div className="time-entry-detail-view">
                    <div className="detail-header">
                      <div className="detail-date">
                        {groupedEntries[selectedDay].date.toLocaleDateString('en-US', {
                          weekday: 'long', 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric'
                        })}
                      </div>
                      <button className="detail-close" onClick={() => setSelectedDay(null)}>×</button>
                    </div>
                    <div className="detail-timeline">
                      {groupedEntries[selectedDay].trips.map((trip:any, tripIdx:any) => {
                        // Filter out "signed in at main office" events
                        const filteredEvents = trip.events.filter((e:any) => !(e.action === 'in' && e.isMainOffice));
                        return (
                          <React.Fragment key={tripIdx}>
                            {filteredEvents.map((event:EventData, eventIdx:number) => (
                              <div key={eventIdx} className="timeline-entry">
                                <div className={`timeline-dot ${event.eventType}`}></div>
                                <div className="timeline-content">
                                  <div className="timeline-time">
                                    {formatTime(event.eventDate)}
                                  </div>
                                  <div className="timeline-label">
                                    {event.eventType === 'in' ? 'Signed In' : 'Signed Out'}
                                    {event.isMainOffice ? ' at Main Office' : ' at Visited Office'}
                                  </div>
                                  <div className="timeline-location">
                                    <span className="timeline-location-icon">
                                      {LOCATION_ICONS[event.location] || '📍'}
                                    </span>
                                    {event.location}
                                  </div>
                                  {eventIdx === filteredEvents.length - 1 && (
                                    <div className="timeline-label" style={{marginTop: '8px'}}>
                                      Total trip time: {formatDuration(trip.duration)}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </React.Fragment>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </>
    );
  };

  export const GuestProfile = ({ user }:any) => {
    const { totalVisits, locationBreakdown } = getGuestProfileStats(user);
    return (
      <div className="profile-content-apple">
        <div className="guest-summary-apple">
          <div className="summary-card-apple">
            <div className="summary-icon-apple">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                   xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 11.99 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 11.99 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20ZM15.88 8.29L10 14.17L8.12 12.29C7.73 11.9 7.1 11.9 6.71 12.29C6.32 12.68 6.32 13.31 6.71 13.7L9.3 16.29C9.69 16.68 10.32 16.68 10.71 16.29L17.3 9.7C17.69 9.31 17.69 8.68 17.3 8.29C16.91 7.9 16.27 7.9 15.88 8.29Z" fill="currentColor"/>
              </svg>
            </div>
            <div className="summary-value-apple large">{totalVisits}</div>
            <div className="summary-label-apple">Total Visits</div>
          </div>
        </div>
        <h3 className="section-title-apple">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
               xmlns="http://www.w3.org/2000/svg" className="section-icon-apple">
            <path d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22C12 22 19 14.25 19 9C19 5.13 15.87 2 11.99 2ZM12 11.5C10.62 11.5 9.5 10.38 9.5 9C9.5 7.62 10.62 6.5 12 6.5C13.38 6.5 14.5 7.62 14.5 9C14.5 10.38 13.38 11.5 12 11.5Z" fill="currentColor"/>
          </svg>
          Locations Visited
        </h3>
        {Object.keys(locationBreakdown).length === 0 ? (
          <div className="no-data-apple">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none"
                 xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22C12 22 19 14.25 19 9C19 5.13 15.87 2 11.99 2ZM12 11.5C10.62 11.5 9.5 10.38 9.5 9C9.5 7.62 10.62 6.5 12 6.5C13.38 6.5 14.5 7.62 14.5 9C14.5 10.38 13.38 11.5 12 11.5Z" fill="currentColor"/>
            </svg>
            <p>No location data available for this period</p>
          </div>
        ) : (
            <div className="location-cards-apple">
            {Object.entries(locationBreakdown as Record<string, number>).map(([loc, count], i) => {
              const total: number = Object.values(locationBreakdown as Record<string, number>).reduce((sum: number, l: number) => sum + l, 0);
              const pct: number = Math.round((count / total) * 100);
              return (
              <div key={i} className="location-card-apple">
                <div className="location-info-apple">
                <div className="location-name-apple">
                  <span className="location-icon-apple">{LOCATION_ICONS[loc as keyof typeof LOCATION_ICONS] || "📍"}</span>
                  {loc}
                </div>
                <div className="location-visits-apple">
                  {count} visit{count !== 1 ? 's' : ''}
                </div>
                </div>
                <div className="percentage-container-apple">
                <div className="percentage-value-apple">{pct}%</div>
                <div className="percentage-bar-apple">
                  <div className="percentage-fill-apple" style={{ width: `${pct}%` }}></div>
                </div>
                </div>
              </div>
              );
            })}
            </div>
        )}
      </div>
    );
  };
